import { useRef, useEffect, useLayoutEffect } from 'react'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import Box from '@mui/material/Box'
import { SxProps } from '@mui/system'
import { Theme } from '@mui/material/styles'

am4core.useTheme(am4themes_animated)

type BarChartProps = {
    chartName: string
    chartData: Array<any>
    dataFieldsCategory: string
    titleTextHead?: string
    titleText?: string
    titleTextSize?: number
    columnColor: string
    responsive?: boolean
    sxProps?: SxProps<Theme>
}

const BarChart = (props: BarChartProps) => {
    const { responsive = false } = props
    let refChart = useRef<am4charts.XYChart>()

    const createChart = () => {
        let chart = am4core.create(props.chartName, am4charts.XYChart)
        refChart.current = chart
        chart.data = props.chartData
        chart.logo.disabled = true
        chart.responsive.enabled = responsive
        chart.maskBullets = false

        if (props.titleText) {
            let title = chart.titles.create()
            title.text = props.titleText
            title.fontSize = props?.titleTextSize ?? 14
            title.fontWeight = 'bold'
            title.wrap = true
            title.marginBottom = 20
        }

        if (props.titleTextHead) {
            let titleHead = chart.titles.create()
            titleHead.text = props.titleTextHead
            titleHead.fontSize = props?.titleTextSize ?? 14
            titleHead.fontWeight = 'bold'
            titleHead.wrap = true
        }

        let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
        categoryAxis.dataFields.category = props.dataFieldsCategory
        categoryAxis.renderer.grid.template.location = 10
        // categoryAxis.renderer.minGridDistance = 100

        // Configure axis label
        let label = categoryAxis.renderer.labels.template
        label.truncate = true
        label.maxWidth = 200
        label.tooltipText = '{category}'

        let valueAxis = chart.xAxes.push(new am4charts.ValueAxis())

        const createGrid = (value: number) => {
            let range = valueAxis.axisRanges.create()
            range.value = value
            range.label.text = '{value}'
        }

        valueAxis.renderer.minGridDistance = 30
        valueAxis.renderer.grid.template.disabled = true
        valueAxis.renderer.labels.template.disabled = true
        valueAxis.events.on('ready', function (ev) {
            ev.target.min = 0
            ev.target.max = ev.target.max as number
            valueAxis.strictMinMax = true
            createGrid(0)
            createGrid(ev.target.max)
        })

        let range = valueAxis.axisRanges.create()
        // range.value = value
        range.label.text = '{value}'

        let series = chart.series.push(new am4charts.ColumnSeries())
        series.columns.template.fill = am4core.color(props.columnColor)
        if (props.chartData.length <= 2) {
            series.columns.template.height = 50
        }
        series.dataFields.valueX = 'value'
        series.dataFields.categoryY = props.dataFieldsCategory
        series.columns.template.tooltipText = '{value}'

        let valueLabel = series.bullets.push(new am4charts.LabelBullet())
        valueLabel.label.text = '[bold]{value}'
        valueLabel.label.fontSize = 12
        valueLabel.label.dx = 15
    }

    useLayoutEffect(() => {
        createChart()
        return () => {
            refChart.current?.dispose()
        }
    }, [props.chartData, props.columnColor])

    return <Box id={props.chartName} sx={props.sxProps}></Box>
}

export default BarChart
